import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { API_BASE } from "../config";
import { apiFetch } from "../utilities/apiFetch";
import { TIPOS_CASO, ETIQUETAS_FUENTE, oficialDeBd } from "../utilities/conciliacionBancaria";
import { ESTADOS_CASO, ESTADOS_ABIERTOS, ACCIONES_CASO } from "../utilities/casosPlantel";
import VistaReasignaciones from "./VistaReasignaciones";

// Modulo "Conciliacion" -> pestaña "Seguimiento de casos" (solo Administrador). Financieros ve los casos que
// publico a cada plantel, que hizo el plantel con cada uno (con la bitacora: antes, despues y justificacion) y
// puede cerrar los que da por resueltos. Los que el cruce con el banco ya no marca se confirman solos al revisar
// en la pestaña Bancos.

const dinero = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fechaCorta = (iso) => (iso ? `${String(iso).substring(8, 10)}/${String(iso).substring(5, 7)}/${String(iso).substring(0, 4)}` : "");
const fechaHora = (ts) => (ts ? `${fechaCorta(ts)} ${String(ts).substring(11, 16)}` : "");
const estiloTh = { textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #ddd", fontSize: "0.8rem", whiteSpace: "nowrap" };
const estiloTd = { padding: "6px 8px", borderBottom: "1px solid #eee", fontSize: "0.85rem", verticalAlign: "top" };
const estiloInput = { padding: "6px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "0.9rem" };
const METODOS = { 1: "ventanilla", 2: "Pinpad", 3: "TPV", 4: "en línea" };
// Lineas de la bitacora que no son un boton del cajero (Reasignar escribe una por orden)
const ACCION_BITACORA = { REASIGNAR_ORIGEN: "Reasignar: el pago salió de esta orden", REASIGNAR_DESTINO: "Reasignar: el pago pasó a esta orden" };

const leerJson = (t) => { try { return t ? JSON.parse(t) : null; } catch (e) { return null; } };

// Lo que cambio en la orden, en palabras
export function cambiosDeOrden(antes, despues) {
  const a = leerJson(antes) || {};
  const d = leerJson(despues) || {};
  const cambios = [];
  if (a.estatus !== d.estatus && (a.estatus || d.estatus)) cambios.push(`estatus ${a.estatus || "—"} → ${d.estatus || "—"}`);
  if ((a.folio_banco || "") !== (d.folio_banco || "")) cambios.push(`capturado ${a.folio_banco || "(vacío)"} → ${d.folio_banco || "(vacío)"}`);
  const metodo = (o) => (o.id_origen ? `${METODOS[o.id_origen] || o.id_origen}${String(o.id_origen) === "1" ? (String(o.id_banco) === "2" ? " BBVA" : " Banorte") : ""}` : "—");
  if (String(a.id_origen || "") !== String(d.id_origen || "") || String(a.id_banco || "") !== String(d.id_banco || "")) cambios.push(`método ${metodo(a)} → ${metodo(d)}`);
  return cambios.join("; ");
}

const VistaSeguimientoCasos = ({ catalogoPlanteles = [] }) => {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);
  const [filtroPlantel, setFiltroPlantel] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("ABIERTOS");

  const nombresPlanteles = useMemo(() => {
    const nombres = {};
    catalogoPlanteles.forEach((p) => { nombres[oficialDeBd(p.id)] = String(p.nombre_plantel || "").trim(); });
    return nombres;
  }, [catalogoPlanteles]);

  const cargar = async () => {
    setCargando(true);
    setError("");
    try {
      const res = await apiFetch(`${API_BASE}/wMultipagosCasosSeguimiento.php`);
      const json = await res.json();
      if (!json.success) { setError(json.message || "No se pudo consultar el seguimiento."); return; }
      setDatos(json.data);
    } catch (e) {
      setError("No hubo respuesta del servidor. Intenta de nuevo.");
    } finally {
      setCargando(false);
    }
  };
  useEffect(() => { cargar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cerrar = async (caso) => {
    const { value: nota, isConfirmed } = await Swal.fire({
      title: `Cerrar el caso ${caso.id}`,
      input: "textarea",
      inputLabel: "¿Por qué se da por resuelto?",
      inputValidator: (v) => (!v || v.trim().length < 5 ? "Escribe el motivo." : undefined),
      showCancelButton: true, confirmButtonText: "Cerrar caso", cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;
    const res = await apiFetch(`${API_BASE}/wMultipagosCasosCerrar.php`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids: [caso.id], estado: "CERRADO", nota }),
    });
    const json = await res.json();
    if (!json.success) { Swal.fire("No se cerró", json.message || "", "error"); return; }
    cargar();
  };

  const bitacoraDe = useMemo(() => {
    const mapa = {};
    (datos ? datos.bitacora : []).forEach((b) => { (mapa[b.id_caso] = mapa[b.id_caso] || []).push(b); });
    return mapa;
  }, [datos]);

  const casos = datos ? datos.casos : [];
  const abierto = (c) => ESTADOS_ABIERTOS.includes(c.estado);
  const visibles = casos.filter((c) => (!filtroPlantel || oficialDeBd(c.id_plantel) === filtroPlantel)
    && (filtroEstado === "" || (filtroEstado === "ABIERTOS" ? abierto(c) : filtroEstado === "VENCIDOS" ? abierto(c) && c.vencido : c.estado === filtroEstado)));
  const cuenta = (f) => casos.filter(f).length;
  const planteles = [...new Set(casos.map((c) => oficialDeBd(c.id_plantel)))].sort();

  return (
    <div className="Dashboard-card">
      <h3 style={{ marginBottom: "5px", color: "#003b5c" }}>Seguimiento de casos publicados</h3>
      <p style={{ color: "#666", fontSize: "0.9rem", marginTop: 0 }}>
        Lo que hizo cada plantel con sus casos. Los corregidos se confirman solos cuando, al revisar en la pestaña Bancos, el cruce ya
        no los marca. Los respondidos los cierras tú cuando estés de acuerdo.
      </p>
      <VistaReasignaciones nombresPlanteles={nombresPlanteles} />
      {error && <div style={{ padding: "10px", backgroundColor: "#fdecea", border: "1px solid #f5c6cb", color: "#7a1c1c", borderRadius: "4px", fontSize: "0.85rem" }}>{error}</div>}
      {cargando && <p style={{ fontSize: "0.85rem" }}>Cargando...</p>}

      {datos && (
        <>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", margin: "10px 0", fontSize: "0.85rem" }}>
            <span><b>{cuenta(abierto)}</b> abiertos</span>·
            <span style={{ color: "#c62828" }}><b>{cuenta((c) => abierto(c) && c.vencido)}</b> vencidos</span>·
            {Object.keys(ESTADOS_CASO).map((e) => <span key={e} style={{ color: ESTADOS_CASO[e].color }}><b>{cuenta((c) => c.estado === e)}</b> {ESTADOS_CASO[e].titulo.toLowerCase()}</span>)}
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", margin: "6px 0 10px 0" }}>
            <select aria-label="Filtrar seguimiento por plantel" value={filtroPlantel} onChange={(e) => setFiltroPlantel(e.target.value)} style={estiloInput}>
              <option value="">Todos los planteles</option>
              {planteles.map((p) => <option key={p} value={p}>{p} {nombresPlanteles[p] || ""}</option>)}
            </select>
            <select aria-label="Filtrar seguimiento por estado" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} style={estiloInput}>
              <option value="ABIERTOS">Abiertos</option>
              <option value="VENCIDOS">Vencidos (abiertos)</option>
              {Object.keys(ESTADOS_CASO).map((e) => <option key={e} value={e}>{ESTADOS_CASO[e].titulo}</option>)}
              <option value="">Todos</option>
            </select>
            <button onClick={cargar} className="btn-select" style={{ border: "1px solid #003b5c", padding: "6px 12px", backgroundColor: "#fff", color: "#003b5c" }}>Actualizar</button>
          </div>

          {!visibles.length ? <p style={{ fontSize: "0.85rem", color: "#666" }}>No hay casos con ese filtro.</p> : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead><tr>{["Plantel", "Caso", "Cobro / orden", "Publicado", "Vence", "Estado", "Lo que hizo el plantel", ""].map((t) => <th key={t} style={estiloTh}>{t}</th>)}</tr></thead>
                <tbody>
                  {visibles.map((c) => {
                    const d = c.datos || {};
                    const oficial = oficialDeBd(c.id_plantel);
                    return (
                      <tr key={c.id}>
                        <td style={estiloTd}>{oficial} {nombresPlanteles[oficial] || ""}</td>
                        <td style={estiloTd}>{(TIPOS_CASO[c.tipo] || { titulo: c.tipo }).titulo}<div style={{ fontSize: "0.75rem", color: "#666" }}>{d.accion}</div></td>
                        <td style={estiloTd}>
                          {fechaCorta(d.fecha)} · {dinero(d.importe)} · {d.medioCobro || ETIQUETAS_FUENTE[c.fuente] || c.fuente}
                          {(d.autorizacion || d.folioBanco) && <div style={{ fontSize: "0.75rem" }}>{d.autorizacion ? `Aut. ${d.autorizacion}` : `Folio ${d.folioBanco}`}</div>}
                          {c.id_intencion && <div style={{ fontSize: "0.75rem" }}>Orden {c.id_intencion}</div>}
                        </td>
                        <td style={estiloTd}>{fechaHora(c.fecha_publicacion)}</td>
                        <td style={{ ...estiloTd, color: abierto(c) && c.vencido ? "#c62828" : "inherit", fontWeight: abierto(c) && c.vencido ? "bold" : "normal" }}>
                          {fechaCorta(c.fecha_limite)}{abierto(c) && c.vencido ? " (vencido)" : ""}
                        </td>
                        <td style={{ ...estiloTd, color: (ESTADOS_CASO[c.estado] || {}).color }}>
                          {(ESTADOS_CASO[c.estado] || { titulo: c.estado }).titulo}
                          {c.nota_cierre && <div style={{ fontSize: "0.75rem", color: "#666" }}>{c.nota_cierre}{c.cerro ? ` (${c.cerro})` : ""}</div>}
                        </td>
                        <td style={estiloTd}>
                          {(bitacoraDe[c.id] || []).map((b) => (
                            <div key={b.id} style={{ marginBottom: "4px" }}>
                              <b>{(ACCIONES_CASO[b.accion] || { boton: ACCION_BITACORA[b.accion] || b.accion }).boton}</b> · {b.usuario} · {fechaHora(b.fecha)}
                              <div style={{ fontSize: "0.75rem" }}>“{b.motivo}”</div>
                              {cambiosDeOrden(b.antes, b.despues) && <div style={{ fontSize: "0.75rem", color: "#555" }}>Orden {b.id_intencion}: {cambiosDeOrden(b.antes, b.despues)}</div>}
                            </div>
                          ))}
                          {!(bitacoraDe[c.id] || []).length && <span style={{ color: "#999" }}>Sin atender</span>}
                        </td>
                        <td style={estiloTd}>
                          {abierto(c) && <button onClick={() => cerrar(c)} className="btn-select" aria-label={`Cerrar el caso ${c.id}`} style={{ border: "1px solid #555", padding: "2px 8px", backgroundColor: "#fff", color: "#333" }}>Cerrar</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VistaSeguimientoCasos;
